import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	Box,
	Typography,
	Button,
	Card,
	CardContent,
	Grid,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Menu,
	MenuItem,
	FormControl,
	InputLabel,
	Select,
	FormControlLabel,
	Switch,
	CircularProgress,
	Alert,
	Autocomplete,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import {
	Add,
	PlayArrow,
	MoreVert,
	Edit,
	Delete,
	Archive,
	CheckCircle,
	Error as ErrorIcon,
	Schedule,
	Repeat,
	ContentCopy,
	NotificationsActive,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
	fetchSuites,
	createSuite,
	updateSuite,
	deleteSuite,
	executeSuite,
	archiveSuite,
	previewDynamicTests,
	fetchSchedules,
	createSchedule,
	updateSchedule as updateScheduleThunk,
	toggleSchedule,
	deleteSchedule,
} from "../store/slices/suiteSlice";
import { fetchProjects } from "../store/slices/projectSlice";
import { fetchFolders } from "../store/slices/folderSlice";
import testService from "../services/testService";
import { formatDateTime } from "../utils/dateUtils";
import { canModify } from "../utils/permissions";
import CloneDialog from "../components/CloneDialog";
import OwnerBadge from "../components/OwnerBadge";
import SuiteNotificationsDialog from "../components/SuiteNotificationsDialog";

export default function Suites() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const { suites, loading, schedules } = useSelector((state) => state.suites);
	const { projects } = useSelector((state) => state.projects);
	const { folders } = useSelector((state) => state.folders);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [notificationsSuite, setNotificationsSuite] = useState(null);
	const [selectedSuite, setSelectedSuite] = useState(null);
	const [cloneTarget, setCloneTarget] = useState(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [executing, setExecuting] = useState({});
	const [availableTests, setAvailableTests] = useState([]);
	const [availableFolders, setAvailableFolders] = useState([]);
	const [previewTests, setPreviewTests] = useState([]);
	const [loadingPreview, setLoadingPreview] = useState(false);

	// Loop state
	const [loopDialogOpen, setLoopDialogOpen] = useState(false);
	const [loopSuite, setLoopSuite] = useState(null);
	const [loopCount, setLoopCount] = useState(3);
	const [loopMode, setLoopMode] = useState("sequence");

	// Schedule state
	const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
	const [scheduleSuite, setScheduleSuite] = useState(null);
	const [scheduleForm, setScheduleForm] = useState({
		type: "ONE_TIME",
		scheduledAt: "",
		intervalMinutes: 60,
		recurringType: "INTERVAL",
		runTime: "09:00",
		weekDay: "MONDAY",
		monthDay: 1,
	});
	const [editingScheduleId, setEditingScheduleId] = useState(null);

	const [formData, setFormData] = useState({
		name: "",
		description: "",
		projectId: "",
		type: "STATIC",
		testIds: [],
		criteria: {
			projectIds: [],
			folderIds: [],
			tags: [],
			status: "",
			namePattern: "",
		},
		executionConfig: {
			browser: "chromium",
			environment: "dev",
			parallelExecution: false,
			stopOnFailure: false,
			captureScreenshots: true,
			captureVideos: false,
		},
	});

	useEffect(() => {
		dispatch(fetchSuites({ workspaceId: "default" }));
		dispatch(fetchProjects());
		dispatch(fetchFolders());
		dispatch(fetchSchedules());
	}, [dispatch]);

	useEffect(() => {
		if (dialogOpen) {
			loadAvailableTests();
			loadAvailableFolders();
		}
	}, [dialogOpen]);

	useEffect(() => {
		if (dialogOpen && formData.projectId) {
			loadAvailableTests();
			loadAvailableFolders();
		}
	}, [formData.projectId]);

	// Preview dynamic tests when criteria changes
	useEffect(() => {
		if (dialogOpen && formData.type === "DYNAMIC") {
			const debounceTimer = setTimeout(() => {
				loadPreviewTests();
			}, 500);
			return () => clearTimeout(debounceTimer);
		} else {
			setPreviewTests([]);
		}
	}, [
		dialogOpen,
		formData.type,
		formData.criteria.projectIds,
		formData.criteria.folderIds,
		formData.criteria.tags,
		formData.criteria.status,
		formData.criteria.namePattern,
	]);

	const loadPreviewTests = async () => {
		try {
			setLoadingPreview(true);
			// Build criteria with converted regex pattern
			const criteria = {
				...formData.criteria,
				namePattern: wildcardToRegex(formData.criteria.namePattern),
			};
			// Only call API if at least one criteria is set
			const hasCriteria =
				(criteria.projectIds && criteria.projectIds.length > 0) ||
				(criteria.folderIds && criteria.folderIds.length > 0) ||
				(criteria.tags && criteria.tags.length > 0) ||
				criteria.status ||
				criteria.namePattern;

			if (hasCriteria) {
				const result = await dispatch(
					previewDynamicTests(criteria),
				).unwrap();
				setPreviewTests(result || []);
			} else {
				setPreviewTests([]);
			}
		} catch (error) {
			console.error("Failed to preview tests:", error);
			setPreviewTests([]);
		} finally {
			setLoadingPreview(false);
		}
	};

	const loadAvailableTests = async () => {
		try {
			const params = { size: 1000 }; // Get all tests
			if (formData.projectId) {
				params.projectId = formData.projectId;
			}
			const response = await testService.getTests(params);
			// Response is a Page object with content array
			setAvailableTests(
				response.content || response.data?.content || response || [],
			);
		} catch (error) {
			console.error("Failed to load tests:", error);
		}
	};

	const loadAvailableFolders = async () => {
		try {
			if (formData.projectId) {
				// Fetch folders for the specific project
				const result = await dispatch(
					fetchFolders(formData.projectId),
				).unwrap();
				setAvailableFolders(result.folders || []);
			} else {
				// Use folders from Redux store if no project selected
				setAvailableFolders(folders || []);
			}
		} catch (error) {
			console.error("Failed to load folders:", error);
			setAvailableFolders([]);
		}
	};

	// Convert wildcard pattern to regex pattern
	const wildcardToRegex = (pattern) => {
		if (!pattern) return "";
		// If pattern is just *, match everything
		if (pattern.trim() === "*") {
			return ".*";
		}
		// If pattern already looks like regex (contains .* or starts/ends with ^$), use as-is
		if (
			pattern.includes(".*") ||
			pattern.startsWith("^") ||
			pattern.endsWith("$")
		) {
			return pattern;
		}
		// Escape special regex characters except *
		let regex = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
		// Replace * with .*
		regex = regex.replace(/\*/g, ".*");
		return regex;
	};

	const handleCreateSuite = async () => {
		try {
			// Prepare form data with deep copy of criteria
			const suiteData = {
				...formData,
				criteria: formData.criteria ? { ...formData.criteria } : null,
			};
			if (
				suiteData.type === "DYNAMIC" &&
				suiteData.criteria?.namePattern
			) {
				suiteData.criteria.namePattern = wildcardToRegex(
					suiteData.criteria.namePattern,
				);
			}

			if (editMode) {
				await dispatch(
					updateSuite({
						id: selectedSuite.id,
						suite: {
							...suiteData,
							userId:
								localStorage.getItem("userId") ||
								"default-user",
						},
					}),
				).unwrap();
				enqueueSnackbar("Suite updated successfully", {
					variant: "success",
				});
			} else {
				await dispatch(
					createSuite({
						...suiteData,
						workspaceId: "default",
						userId:
							localStorage.getItem("userId") || "default-user",
					}),
				).unwrap();
				enqueueSnackbar("Suite created successfully", {
					variant: "success",
				});
			}
			setDialogOpen(false);
			resetForm();
		} catch (error) {
			enqueueSnackbar(
				editMode ? "Failed to update suite" : "Failed to create suite",
				{ variant: "error" },
			);
		}
	};

	const handleExecuteSuite = async (suite) => {
		setExecuting({ ...executing, [suite.id]: true });
		try {
			const suiteExecution = await dispatch(
				executeSuite(suite.id),
			).unwrap();
			enqueueSnackbar("Suite execution started", { variant: "success" });
			navigate(`/suite-executions?suiteExecutionId=${suiteExecution.id}`);
		} catch (error) {
			enqueueSnackbar("Failed to execute suite", { variant: "error" });
		} finally {
			setExecuting({ ...executing, [suite.id]: false });
		}
	};

	const handleOpenLoop = (suite) => {
		setLoopSuite(suite);
		setLoopCount(3);
		setLoopMode("sequence");
		setLoopDialogOpen(true);
		handleMenuClose();
	};

	const handleLoopExecute = () => {
		setLoopDialogOpen(false);
		navigate("/suite-executions", { state: { loopStarted: true } });

		if (loopMode === "parallel") {
			enqueueSnackbar(`Starting ${loopCount} parallel suite executions...`, { variant: "info" });
			Promise.allSettled(
				Array.from({ length: loopCount }, () => dispatch(executeSuite(loopSuite.id)).unwrap())
			).then((results) => {
				const ok = results.filter((r) => r.status === "fulfilled").length;
				enqueueSnackbar(`${ok} of ${loopCount} suite executions started`, { variant: "success" });
				dispatch(fetchSuites({ workspaceId: "default" }));
			});
		} else {
			enqueueSnackbar(`Starting ${loopCount} sequential suite executions...`, { variant: "info" });
			(async () => {
				for (let i = 0; i < loopCount; i++) {
					try {
						const suiteExecution = await dispatch(executeSuite(loopSuite.id)).unwrap();
						if (i < loopCount - 1) {
							await testService.pollSuiteExecutionUntilComplete(suiteExecution.id);
						}
					} catch (e) {
						console.error(`Suite loop iteration ${i + 1} failed:`, e.message);
						break;
					}
				}
				dispatch(fetchSuites({ workspaceId: "default" }));
			})();
		}
	};

	const handleDeleteSuite = async () => {
		try {
			await dispatch(deleteSuite(selectedSuite.id)).unwrap();
			enqueueSnackbar("Suite deleted successfully", {
				variant: "success",
			});
			setDeleteDialogOpen(false);
			setSelectedSuite(null);
		} catch (error) {
			enqueueSnackbar("Failed to delete suite", { variant: "error" });
		}
	};

	const handleOpenSchedule = (suite) => {
		setScheduleSuite(suite);
		setEditingScheduleId(null);
		const existing = schedules.find(
			(s) => s.suiteId === suite.id && s.enabled,
		);
		if (existing) {
			setEditingScheduleId(existing.id);
			setScheduleForm({
				type: existing.type || "ONE_TIME",
				scheduledAt: existing.scheduledAt
					? existing.scheduledAt.substring(0, 16)
					: "",
				intervalMinutes: existing.intervalMinutes || 60,
				recurringType: existing.recurringType || "INTERVAL",
				runTime: existing.runTime || "09:00",
				weekDay: existing.weekDay || "MONDAY",
				monthDay: existing.monthDay || 1,
			});
		} else {
			setScheduleForm({
				type: "ONE_TIME",
				scheduledAt: "",
				intervalMinutes: 60,
				recurringType: "INTERVAL",
				runTime: "09:00",
				weekDay: "MONDAY",
				monthDay: 1,
			});
		}
		setScheduleDialogOpen(true);
		handleMenuClose();
	};

	const handleSaveSchedule = async () => {
		try {
			const payload = {
				suiteId: scheduleSuite.id,
				type: scheduleForm.type,
				enabled: true,
			};
			if (scheduleForm.type === "ONE_TIME") {
				payload.scheduledAt = scheduleForm.scheduledAt;
			} else {
				payload.recurringType = scheduleForm.recurringType;
				if (scheduleForm.recurringType === "INTERVAL") {
					payload.intervalMinutes = scheduleForm.intervalMinutes;
				} else {
					payload.runTime = scheduleForm.runTime;
					if (scheduleForm.recurringType === "WEEKLY") {
						payload.weekDay = scheduleForm.weekDay;
					}
					if (scheduleForm.recurringType === "MONTHLY") {
						payload.monthDay = Number(scheduleForm.monthDay);
					}
				}
			}

			if (editingScheduleId) {
				await dispatch(
					updateScheduleThunk({
						id: editingScheduleId,
						schedule: payload,
					}),
				).unwrap();
				enqueueSnackbar("Schedule updated", { variant: "success" });
			} else {
				await dispatch(createSchedule(payload)).unwrap();
				enqueueSnackbar("Schedule created", { variant: "success" });
			}
			setScheduleDialogOpen(false);
		} catch (error) {
			enqueueSnackbar("Failed to save schedule", { variant: "error" });
		}
	};

	const handleDeleteSchedule = async () => {
		if (!editingScheduleId) return;
		try {
			await dispatch(deleteSchedule(editingScheduleId)).unwrap();
			enqueueSnackbar("Schedule removed", { variant: "success" });
			setScheduleDialogOpen(false);
		} catch (error) {
			enqueueSnackbar("Failed to delete schedule", { variant: "error" });
		}
	};

	const handleMenuClick = (event, suite) => {
		setAnchorEl(event.currentTarget);
		setSelectedSuite(suite);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const resetForm = () => {
		setFormData({
			name: "",
			description: "",
			projectId: "",
			type: "STATIC",
			testIds: [],
			criteria: {
				projectIds: [],
				folderIds: [],
				tags: [],
				status: "",
				namePattern: "",
			},
			executionConfig: {
				browser: "chromium",
				environment: "dev",
				parallelExecution: false,
				stopOnFailure: false,
				captureScreenshots: true,
				captureVideos: false,
			},
		});
		setEditMode(false);
		setSelectedSuite(null);
	};

	const handleEditSuite = (suite) => {
		setEditMode(true);
		setSelectedSuite(suite);
		setFormData({
			name: suite.name || "",
			description: suite.description || "",
			projectId: suite.projectId || "",
			type: suite.type || "STATIC",
			testIds: suite.testIds || [],
			criteria: suite.criteria || {
				projectIds: [],
				folderIds: [],
				tags: [],
				status: "",
				namePattern: "",
			},
			executionConfig: suite.executionConfig || {
				browser: "chromium",
				environment: "dev",
				parallelExecution: false,
				stopOnFailure: false,
				captureScreenshots: true,
				captureVideos: false,
			},
		});
		setDialogOpen(true);
		handleMenuClose();
	};

	const getStatusColor = (stats) => {
		if (!stats || !stats.lastExecutionTestCount) return "default";
		const passRate =
			(stats.lastExecutionPassCount / stats.lastExecutionTestCount) * 100;
		if (passRate === 100) return "success";
		if (passRate >= 80) return "warning";
		return "error";
	};

	const getSuiteSchedule = (suiteId) =>
		schedules.find((s) => s.suiteId === suiteId && s.enabled);

	return (
		<Box sx={{ p: 3 }}>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 4,
				}}
			>
				<Box>
					<Typography variant="h4" fontWeight={700}>
						Test Suites
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Group and execute multiple tests together
					</Typography>
				</Box>
				<Button
					variant="contained"
					startIcon={<Add />}
					onClick={() => setDialogOpen(true)}
				>
					New Suite
				</Button>
			</Box>

			{/* Suites Grid */}
			{loading ? (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						p: 8,
					}}
				>
					<CircularProgress />
				</Box>
			) : suites.length === 0 ? (
				<Alert severity="info">
					No test suites yet. Create your first suite to group and run
					tests together.
				</Alert>
			) : (
				<Grid container spacing={3}>
					{suites.map((suite) => (
						<Grid item xs={12} md={6} lg={4} key={suite.id}>
							<Card
								sx={{
									height: "100%",
									display: "flex",
									flexDirection: "column",
									"&:hover": { boxShadow: 6 },
								}}
							>
								<CardContent sx={{ flexGrow: 1 }}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											mb: 2,
										}}
									>
										<Box sx={{ flexGrow: 1 }}>
											<Typography
												variant="h6"
												fontWeight={600}
											>
												{suite.name}
											</Typography>
											<Typography
												variant="body2"
												color="text.secondary"
											>
												{suite.description ||
													"No description"}
											</Typography>
											<OwnerBadge item={suite} sx={{ mt: 0.5 }} />
										</Box>
										<IconButton
											size="small"
											onClick={(e) =>
												handleMenuClick(e, suite)
											}
										>
											<MoreVert />
										</IconButton>
									</Box>

									<Box
										sx={{
											display: "flex",
											gap: 1,
											mb: 2,
											flexWrap: "wrap",
										}}
									>
										<Chip
											label={suite.type}
											size="small"
											color="primary"
											variant="outlined"
										/>
										<Chip
											label={`${suite.stats?.lastExecutionTestCount || suite.testIds?.length || 0} tests`}
											size="small"
										/>
										{suite.stats?.lastExecutedAt && (
											<Chip
												icon={
													getStatusColor(
														suite.stats,
													) === "success" ? (
														<CheckCircle />
													) : (
														<ErrorIcon />
													)
												}
												label={`${suite.stats.lastExecutionPassCount || 0}/${suite.stats.lastExecutionTestCount || 0} passed`}
												size="small"
												color={getStatusColor(
													suite.stats,
												)}
											/>
										)}
										{getSuiteSchedule(suite.id) && (
											<Chip
												icon={<Schedule />}
												label="Scheduled"
												size="small"
												color="info"
												variant="outlined"
											/>
										)}
									</Box>

									{suite.stats?.lastExecutedAt && (
										<Typography
											variant="caption"
											color="text.secondary"
										>
											Last run:{" "}
											{formatDateTime(suite.stats.lastExecutedAt)}
										</Typography>
									)}
								</CardContent>

								<Box
									sx={{
										p: 2,
										pt: 0,
										display: "flex",
										gap: 1,
									}}
								>
									<Button
										variant="contained"
										startIcon={
											executing[suite.id] ? (
												<CircularProgress size={16} />
											) : (
												<PlayArrow />
											)
										}
										onClick={() =>
											handleExecuteSuite(suite)
										}
										disabled={executing[suite.id]}
										fullWidth
									>
										Run
									</Button>
								</Box>
							</Card>
						</Grid>
					))}
				</Grid>
			)}

			{/* Create/Edit Suite Dialog */}
			<Dialog
				open={dialogOpen}
				onClose={() => {
					setDialogOpen(false);
					resetForm();
				}}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					{editMode ? "Edit Test Suite" : "Create New Test Suite"}
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
							mt: 2,
						}}
					>
						<TextField
							label="Suite Name"
							value={formData.name}
							onChange={(e) =>
								setFormData({
									...formData,
									name: e.target.value,
								})
							}
							required
							fullWidth
						/>
						<TextField
							label="Description"
							value={formData.description}
							onChange={(e) =>
								setFormData({
									...formData,
									description: e.target.value,
								})
							}
							multiline
							rows={3}
							fullWidth
						/>
						<FormControl fullWidth>
							<InputLabel>Project</InputLabel>
							<Select
								value={formData.projectId}
								onChange={(e) =>
									setFormData({
										...formData,
										projectId: e.target.value,
									})
								}
								label="Project"
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{projects.filter(canModify).map((project) => (
									<MenuItem
										key={project.id}
										value={project.id}
									>
										{project.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl fullWidth>
							<InputLabel>Suite Type</InputLabel>
							<Select
								value={formData.type}
								onChange={(e) =>
									setFormData({
										...formData,
										type: e.target.value,
									})
								}
								label="Suite Type"
							>
								<MenuItem value="STATIC">
									Static (Manual Selection)
								</MenuItem>
								<MenuItem value="DYNAMIC">
									Dynamic (Auto-filtered)
								</MenuItem>
							</Select>
						</FormControl>

						{/* Test Selection for STATIC mode */}
						{formData.type === "STATIC" && (
							<Autocomplete
								multiple
								// Own tests only (clone someone else's to use it)
								options={availableTests.filter(
									(test) => canModify(test) || formData.testIds.includes(test.id),
								)}
								getOptionLabel={(option) =>
									option.name || option.id
								}
								value={availableTests.filter((test) =>
									formData.testIds.includes(test.id),
								)}
								onChange={(event, newValue) => {
									setFormData({
										...formData,
										testIds: newValue.map((t) => t.id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Select Tests"
										placeholder="Choose tests to include"
									/>
								)}
								renderTags={(value, getTagProps) =>
									value.map((option, index) => (
										<Chip
											label={option.name}
											{...getTagProps({ index })}
											key={option.id}
										/>
									))
								}
							/>
						)}

						{/* Criteria Configuration for DYNAMIC mode */}
						{formData.type === "DYNAMIC" && (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 2,
									p: 2,
									bgcolor: "action.hover",
									borderRadius: 1,
								}}
							>
								<Typography
									variant="subtitle2"
									color="text.secondary"
								>
									Filter Criteria (Tests matching these
									criteria will be automatically included)
								</Typography>

								<Autocomplete
									multiple
									// A dynamic suite matches its owner's tests only
									options={projects.filter(canModify)}
									getOptionLabel={(option) => option.name}
									value={projects.filter((project) =>
										formData.criteria.projectIds?.includes(
											project.id,
										),
									)}
									onChange={(event, newValue) => {
										setFormData({
											...formData,
											criteria: {
												...formData.criteria,
												projectIds: newValue.map(
													(p) => p.id,
												),
											},
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Projects"
											placeholder="Filter by projects"
											size="small"
										/>
									)}
								/>

								<Autocomplete
									multiple
									options={availableFolders}
									getOptionLabel={(option) => option.name}
									value={availableFolders.filter((folder) =>
										formData.criteria.folderIds?.includes(
											folder.id,
										),
									)}
									onChange={(event, newValue) => {
										setFormData({
											...formData,
											criteria: {
												...formData.criteria,
												folderIds: newValue.map(
													(f) => f.id,
												),
											},
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Folders"
											placeholder="Filter by folders"
											size="small"
										/>
									)}
								/>

								<Autocomplete
									multiple
									freeSolo
									options={[]}
									value={formData.criteria.tags || []}
									onChange={(event, newValue) => {
										setFormData({
											...formData,
											criteria: {
												...formData.criteria,
												tags: newValue,
											},
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Tags"
											placeholder="Add tags to filter"
											size="small"
										/>
									)}
								/>

								<FormControl size="small" fullWidth>
									<InputLabel>Status</InputLabel>
									<Select
										value={formData.criteria.status || ""}
										onChange={(e) =>
											setFormData({
												...formData,
												criteria: {
													...formData.criteria,
													status: e.target.value,
												},
											})
										}
										label="Status"
									>
										<MenuItem value="">
											<em>Any</em>
										</MenuItem>
										<MenuItem value="ACTIVE">
											Active
										</MenuItem>
										<MenuItem value="DRAFT">Draft</MenuItem>
										<MenuItem value="ARCHIVED">
											Archived
										</MenuItem>
									</Select>
								</FormControl>

								<TextField
									label="Name Pattern"
									value={formData.criteria.namePattern || ""}
									onChange={(e) =>
										setFormData({
											...formData,
											criteria: {
												...formData.criteria,
												namePattern: e.target.value,
											},
										})
									}
									placeholder="e.g., login_*, *_test, or just * for all"
									size="small"
									fullWidth
									helperText="Use * as wildcard to match test names"
								/>

								{/* Preview of matching tests */}
								<Box
									sx={{
										mt: 2,
										p: 2,
										bgcolor: "background.paper",
										borderRadius: 1,
										border: "1px solid",
										borderColor: "divider",
									}}
								>
									<Typography
										variant="subtitle2"
										sx={{ mb: 1 }}
									>
										{loadingPreview ? (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
												}}
											>
												<CircularProgress size={16} />
												Loading preview...
											</Box>
										) : (
											`Matching Tests (${previewTests.length})`
										)}
									</Typography>
									{!loadingPreview &&
										previewTests.length === 0 && (
											<Typography
												variant="body2"
												color="text.secondary"
											>
												No tests match the current
												criteria. Add filters above to
												see matching tests.
											</Typography>
										)}
									{!loadingPreview &&
										previewTests.length > 0 && (
											<Box
												sx={{
													maxHeight: 150,
													overflow: "auto",
												}}
											>
												{previewTests
													.slice(0, 10)
													.map((test) => (
														<Chip
															key={test.id}
															label={test.name}
															size="small"
															sx={{ m: 0.5 }}
															variant="outlined"
														/>
													))}
												{previewTests.length > 10 && (
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ ml: 1 }}
													>
														...and{" "}
														{previewTests.length -
															10}{" "}
														more
													</Typography>
												)}
											</Box>
										)}
								</Box>
							</Box>
						)}

						<FormControl fullWidth>
							<InputLabel>Browser</InputLabel>
							<Select
								value={formData.executionConfig.browser}
								onChange={(e) =>
									setFormData({
										...formData,
										executionConfig: {
											...formData.executionConfig,
											browser: e.target.value,
										},
									})
								}
								label="Browser"
							>
								<MenuItem value="chromium">Chromium</MenuItem>
								<MenuItem value="firefox">Firefox</MenuItem>
								<MenuItem value="webkit">WebKit</MenuItem>
							</Select>
						</FormControl>
						<FormControlLabel
							control={
								<Switch
									checked={
										formData.executionConfig
											.parallelExecution
									}
									onChange={(e) =>
										setFormData({
											...formData,
											executionConfig: {
												...formData.executionConfig,
												parallelExecution:
													e.target.checked,
											},
										})
									}
								/>
							}
							label="Parallel Execution (up to the system limit, set by admins)"
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setDialogOpen(false);
							resetForm();
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleCreateSuite}
						disabled={!formData.name.trim()}
					>
						{editMode ? "Update Suite" : "Create Suite"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Context Menu */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
			>
				{/* Owner or admin: edit, schedule, archive, delete. Others run and clone. */}
				{canModify(selectedSuite) && (
					<MenuItem
						onClick={() => {
							handleEditSuite(selectedSuite);
						}}
					>
						<Edit fontSize="small" sx={{ mr: 1 }} />
						Edit
					</MenuItem>
				)}
				{canModify(selectedSuite) && (
					<MenuItem onClick={() => handleOpenSchedule(selectedSuite)}>
						<Schedule fontSize="small" sx={{ mr: 1 }} />
						Schedule
					</MenuItem>
				)}
				{canModify(selectedSuite) && (
					<MenuItem
						onClick={() => {
							setNotificationsSuite(selectedSuite);
							handleMenuClose();
						}}
					>
						<NotificationsActive fontSize="small" sx={{ mr: 1 }} />
						Notifications
					</MenuItem>
				)}
				<MenuItem onClick={() => handleOpenLoop(selectedSuite)}>
					<Repeat fontSize="small" sx={{ mr: 1 }} />
					Run in Loop
				</MenuItem>
				<MenuItem
					onClick={() => {
						setCloneTarget(selectedSuite);
						handleMenuClose();
					}}
				>
					<ContentCopy fontSize="small" sx={{ mr: 1 }} />
					Clone
				</MenuItem>
				{canModify(selectedSuite) && <MenuItem
					onClick={async () => {
						handleMenuClose();
						try {
							await dispatch(
								archiveSuite(selectedSuite.id),
							).unwrap();
							enqueueSnackbar("Suite archived", {
								variant: "success",
							});
						} catch (error) {
							enqueueSnackbar("Failed to archive suite", {
								variant: "error",
							});
						}
					}}
				>
					<Archive fontSize="small" sx={{ mr: 1 }} />
					Archive
				</MenuItem>}
				{canModify(selectedSuite) && (
					<MenuItem
						onClick={() => {
							handleMenuClose();
							setDeleteDialogOpen(true);
						}}
					>
						<Delete fontSize="small" sx={{ mr: 1 }} />
						Delete
					</MenuItem>
				)}
			</Menu>

			<SuiteNotificationsDialog suite={notificationsSuite} open={!!notificationsSuite} onClose={() => setNotificationsSuite(null)} />

			<CloneDialog
				open={!!cloneTarget}
				kind="suite"
				item={cloneTarget}
				onClose={() => setCloneTarget(null)}
				onCloned={() => dispatch(fetchSuites({ workspaceId: "default" }))}
			/>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
			>
				<DialogTitle>Delete Suite?</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete "{selectedSuite?.name}"?
						This action cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleDeleteSuite}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* Schedule Dialog */}
			<Dialog
				open={scheduleDialogOpen}
				onClose={() => setScheduleDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{editingScheduleId ? "Edit Schedule" : "Schedule Suite"}
					{scheduleSuite && (
						<Typography variant="body2" color="text.secondary">
							{scheduleSuite.name}
						</Typography>
					)}
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
							mt: 2,
						}}
					>
						<FormControl fullWidth>
							<InputLabel>Schedule Type</InputLabel>
							<Select
								value={scheduleForm.type}
								onChange={(e) =>
									setScheduleForm({
										...scheduleForm,
										type: e.target.value,
									})
								}
								label="Schedule Type"
							>
								<MenuItem value="ONE_TIME">One-Time</MenuItem>
								<MenuItem value="RECURRING">Recurring</MenuItem>
							</Select>
						</FormControl>

						{scheduleForm.type === "ONE_TIME" ? (
							<TextField
								label="Run At"
								type="datetime-local"
								value={scheduleForm.scheduledAt}
								onChange={(e) =>
									setScheduleForm({
										...scheduleForm,
										scheduledAt: e.target.value,
									})
								}
								fullWidth
								InputLabelProps={{ shrink: true }}
							/>
						) : (
							<>
								<FormControl fullWidth>
									<InputLabel>Frequency</InputLabel>
									<Select
										value={scheduleForm.recurringType}
										onChange={(e) =>
											setScheduleForm({
												...scheduleForm,
												recurringType: e.target.value,
											})
										}
										label="Frequency"
									>
										<MenuItem value="INTERVAL">Every N minutes/hours</MenuItem>
										<MenuItem value="DAILY">Daily</MenuItem>
										<MenuItem value="WEEKLY">Weekly</MenuItem>
										<MenuItem value="MONTHLY">Monthly</MenuItem>
									</Select>
								</FormControl>

								{scheduleForm.recurringType === "INTERVAL" && (
									<FormControl fullWidth>
										<InputLabel>Run Every</InputLabel>
										<Select
											value={scheduleForm.intervalMinutes}
											onChange={(e) =>
												setScheduleForm({
													...scheduleForm,
													intervalMinutes: e.target.value,
												})
											}
											label="Run Every"
										>
											<MenuItem value={30}>30 minutes</MenuItem>
											<MenuItem value={60}>1 hour</MenuItem>
											<MenuItem value={360}>6 hours</MenuItem>
											<MenuItem value={720}>12 hours</MenuItem>
											<MenuItem value={1440}>24 hours</MenuItem>
										</Select>
									</FormControl>
								)}

								{scheduleForm.recurringType === "WEEKLY" && (
									<FormControl fullWidth>
										<InputLabel>Day of Week</InputLabel>
										<Select
											value={scheduleForm.weekDay}
											onChange={(e) =>
												setScheduleForm({
													...scheduleForm,
													weekDay: e.target.value,
												})
											}
											label="Day of Week"
										>
											{["MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY","SATURDAY","SUNDAY"].map((d) => (
												<MenuItem key={d} value={d}>
													{d.charAt(0) + d.slice(1).toLowerCase()}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								)}

								{scheduleForm.recurringType === "MONTHLY" && (
									<TextField
										label="Day of Month"
										type="number"
										value={scheduleForm.monthDay}
										onChange={(e) =>
											setScheduleForm({
												...scheduleForm,
												monthDay: Math.max(1, Math.min(31, parseInt(e.target.value, 10) || 1)),
											})
										}
										fullWidth
										inputProps={{ min: 1, max: 31 }}
									/>
								)}

								{scheduleForm.recurringType !== "INTERVAL" && (
									<TextField
										label="Time"
										type="time"
										value={scheduleForm.runTime}
										onChange={(e) =>
											setScheduleForm({
												...scheduleForm,
												runTime: e.target.value,
											})
										}
										fullWidth
										InputLabelProps={{ shrink: true }}
									/>
								)}
							</>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					{editingScheduleId && (
						<Button
							color="error"
							onClick={handleDeleteSchedule}
							sx={{ mr: "auto" }}
						>
							Remove Schedule
						</Button>
					)}
					<Button onClick={() => setScheduleDialogOpen(false)}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSaveSchedule}
						disabled={
							(scheduleForm.type === "ONE_TIME" && !scheduleForm.scheduledAt) ||
							(scheduleForm.type === "RECURRING" &&
								scheduleForm.recurringType !== "INTERVAL" &&
								!scheduleForm.runTime)
						}
					>
						{editingScheduleId
							? "Update Schedule"
							: "Create Schedule"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Run in Loop Dialog */}
			<Dialog
				open={loopDialogOpen}
				onClose={() => setLoopDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Run Suite in Loop</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						{loopSuite?.name}
					</Typography>
					<TextField
						label="Number of Loops"
						type="number"
						fullWidth
						value={loopCount}
						onChange={(e) => setLoopCount(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
						inputProps={{ min: 1, max: 20 }}
						helperText="Maximum 20 iterations"
						sx={{ mb: 3 }}
					/>
					<Typography variant="body2" sx={{ mb: 1 }}>
						Execution Mode
					</Typography>
					<ToggleButtonGroup
						value={loopMode}
						exclusive
						onChange={(_, val) => val && setLoopMode(val)}
						fullWidth
					>
						<ToggleButton value="sequence">Sequential</ToggleButton>
						<ToggleButton value="parallel">Parallel</ToggleButton>
					</ToggleButtonGroup>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
						{loopMode === "sequence"
							? "Each iteration starts after the previous one completes."
							: "All iterations start simultaneously."}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setLoopDialogOpen(false)}>
						Cancel
					</Button>
					<Button
						onClick={handleLoopExecute}
						variant="contained"
						startIcon={<Repeat />}
					>
						{`Run ${loopCount}×`}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
